import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DinoDuo as DinoDuoType } from "@/types/dinoDuos";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Users, BookOpen, Flame, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { DuoChat } from "@/components/DuoChat";
import { DuoActivityFeed } from "@/components/DuoActivityFeed";
import { DuoCheckInsCard } from "@/components/DuoCheckInsCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

export default function DinoDuo() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [duo, setDuo] = useState<DinoDuoType | null>(null);
  const [loading, setLoading] = useState(true);
  const [newPage, setNewPage] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [friends, setFriends] = useState<any[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (!id || !user) return;

    fetchDuo();
    setupRealtimeSubscription();
  }, [id, user]);

  useEffect(() => {
    if (inviteDialogOpen && user) {
      fetchFriends();
    }
  }, [inviteDialogOpen, user]);

  const fetchDuo = async () => {
    if (!id || !user) return;

    try {
      // Fetch duo details
      const { data: duoData, error: duoError } = await supabase
        .from('buddy_reads')
        .select(`
          *,
          book:books(id, title, authors, cover_url, page_count)
        `)
        .eq('id', id)
        .single();

      if (duoError) throw duoError;

      // Fetch members
      const { data: membersData, error: membersError } = await supabase
        .from('buddy_read_members')
        .select('*')
        .eq('buddy_read_id', id)
        .eq('status', 'accepted');

      if (membersError) throw membersError;

      // Fetch member profiles
      const memberUserIds = Array.from(
        new Set((membersData || []).map((m: any) => m.user_id).filter(Boolean))
      );

      let profileMap: Record<string, any> = {};
      if (memberUserIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, name, image')
          .in('id', memberUserIds);

        if (profilesError) throw profilesError;

        profileMap = (profilesData || []).reduce((acc: Record<string, any>, profile: any) => {
          acc[profile.id] = profile;
          return acc;
        }, {});
      }

      setDuo({
        ...duoData,
        status: duoData.status as 'active' | 'completed' | 'abandoned',
        book: Array.isArray(duoData.book) ? duoData.book[0] : duoData.book,
        members: (membersData || []).map((m: any) => ({
          ...m,
          status: m.status as 'invited' | 'accepted' | 'declined',
          user: profileMap[m.user_id] || null,
        })),
      });
    } catch (error: any) {
      console.error('Error fetching duo:', error);
      toast({
        title: "Error loading duo",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!id) return;

    const channel = supabase
      .channel(`duo-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `buddy_read_id=eq.${id}`
        },
        () => {
          fetchDuo();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_reads',
          filter: `id=eq.${id}`
        },
        () => {
          fetchDuo();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchFriends = async () => {
    if (!user) return;

    try {
      // Get accepted follows
      const { data: followsData, error: followsError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .eq('status', 'accepted');

      if (followsError) throw followsError;

      const friendIds = followsData?.map(f => f.following_id) || [];
      
      if (friendIds.length === 0) {
        setFriends([]);
        return;
      }

      // Get friend profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', friendIds);

      if (profilesError) throw profilesError;

      // Filter out friends who are already in the duo
      const currentMemberIds = duo?.members?.map(m => m.user_id) || [];
      const availableFriends = profilesData?.filter(p => !currentMemberIds.includes(p.id)) || [];

      setFriends(availableFriends);
    } catch (error: any) {
      console.error('Error fetching friends:', error);
    }
  };

  const handleInviteFriends = async () => {
    if (!user || !id || selectedFriends.length === 0) return;

    setInviting(true);
    try {
      const invites = selectedFriends.map(friendId => ({
        buddy_read_id: id,
        user_id: friendId,
        status: 'invited'
      }));

      const { error } = await supabase
        .from('buddy_read_members')
        .insert(invites);

      if (error) throw error;

      toast({
        title: "Invites sent! 🦕",
        description: `Invited ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''} to the duo`
      });

      setInviteDialogOpen(false);
      setSelectedFriends([]);
      fetchDuo();
    } catch (error: any) {
      console.error('Error inviting friends:', error);
      toast({
        title: "Failed to send invites",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!user || !id || !newPage) return;

    const pageNum = parseInt(newPage);
    if (isNaN(pageNum) || pageNum < 0) {
      toast({
        title: "Invalid page number",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({
          current_page: pageNum,
          last_updated: new Date().toISOString()
        })
        .eq('buddy_read_id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Progress updated! 📖",
        description: `You're on page ${pageNum}`
      });

      setNewPage("");
      fetchDuo();
    } catch (error: any) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error updating progress",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="container max-w-7xl mx-auto py-8">
        <p>Loading your Dino Duo...</p>
      </div>
    );
  }

  if (!duo) {
    return (
      <div className="container max-w-7xl mx-auto py-8">
        <p>Duo not found</p>
      </div>
    );
  }

  const myMember = duo.members?.find(m => m.user_id === user?.id);
  const totalPages = duo.book?.page_count || 0;
  const groupProgress = duo.members && duo.members.length > 0
    ? Math.round(
        duo.members.reduce((sum, m) => sum + (m.current_page || 0), 0) / 
        (duo.members.length * totalPages) * 100
      )
    : 0;

  return (
    <div className="container max-w-7xl mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              🦕 {duo.title || duo.book?.title}
            </h1>
            {duo.title && (
              <p className="text-muted-foreground">{duo.book?.title}</p>
            )}
          </div>
        </div>
        
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite More Friends
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Friends to Duo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {friends.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No friends available to invite. Either all your friends are already in this duo or you need to follow more people!
                </p>
              ) : (
                <>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {friends.map((friend) => (
                      <div key={friend.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg">
                        <Checkbox
                          checked={selectedFriends.includes(friend.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedFriends([...selectedFriends, friend.id]);
                            } else {
                              setSelectedFriends(selectedFriends.filter(id => id !== friend.id));
                            }
                          }}
                        />
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={friend.image} />
                          <AvatarFallback>{friend.username?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium">{friend.name || friend.username}</p>
                          {friend.name && (
                            <p className="text-sm text-muted-foreground">@{friend.username}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={handleInviteFriends} 
                    disabled={selectedFriends.length === 0 || inviting}
                    className="w-full"
                  >
                    Invite {selectedFriends.length > 0 ? `${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}` : 'friends'}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Book & Group Overview */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-6">
            <img
              src={duo.book?.cover_url || '/placeholder.svg'}
              alt={duo.book?.title}
              className="w-32 h-48 object-cover rounded shadow-md"
            />
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-xl font-semibold">{duo.book?.title}</h2>
                <p className="text-muted-foreground">
                  by {duo.book?.authors?.join(', ')}
                </p>
              </div>

              {/* Group Avatars */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div className="flex -space-x-2">
                  {duo.members?.map((member) => (
                    <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                      <AvatarImage src={member.user?.image} />
                      <AvatarFallback>
                        {member.user?.username?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <span className="text-sm text-muted-foreground ml-2">
                  {duo.members?.length} readers
                </span>
              </div>

              {/* Member Progress */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Reading Progress</span>
                  <span className="font-medium">{groupProgress}% group average</span>
                </div>
                {duo.members?.map((member) => {
                  const progress = totalPages > 0 ? ((member.current_page || 0) / totalPages) * 100 : 0;
                  return (
                    <div key={member.id} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={member.user?.image} />
                          <AvatarFallback className="text-xs">
                            {member.user?.username?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex items-center justify-between text-sm">
                          <span className="font-medium">
                            {member.user?.name || member.user?.username}
                          </span>
                          <span className="text-muted-foreground">
                            {member.current_page || 0} / {totalPages} • {Math.round(progress)}%
                          </span>
                        </div>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  );
                })}
              </div>

              {/* Streak & Stats */}
              <div className="flex gap-4 text-sm">
                {duo.group_streak > 0 && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Flame className="h-3 w-3" />
                    {duo.group_streak} day streak
                  </Badge>
                )}
                {totalPages > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <BookOpen className="h-4 w-4" />
                    <span>{totalPages} pages</span>
                  </div>
                )}
                {duo.target_end_date && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Target: {format(new Date(duo.target_end_date), 'MMM d')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Update Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Update Your Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="page">Current Page</Label>
              <Input
                id="page"
                type="number"
                placeholder={`Current: ${myMember?.current_page || 0}`}
                value={newPage}
                onChange={(e) => setNewPage(e.target.value)}
                min="0"
                max={totalPages}
              />
            </div>
            <Button onClick={handleUpdateProgress} disabled={!newPage}>
              Update Progress
            </Button>
          </div>

          {myMember && totalPages > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Your progress</span>
                <span className="font-medium">
                  {Math.round(((myMember.current_page || 0) / totalPages) * 100)}%
                </span>
              </div>
              <Progress value={((myMember.current_page || 0) / totalPages) * 100} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grid Layout for Activity, Chat, and Check-ins */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <DuoActivityFeed duoId={id!} />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <DuoChat duoId={id!} />
          <DuoCheckInsCard duoId={id!} />
        </div>
      </div>
    </div>
  );
}
